import { API_ERROR_CODES, type AttemptRejection, type AttemptResult, type PlayWindow } from '@lk/shared';
import { ApiHttpError, NetworkError, humanMessageForCode } from '../lib/errors';
import { postAttempts, type AttemptBatchResponse } from '../lib/api';
import type { LearnKidsDb, QueuedAttempt } from './db';
import {
  BATCH_SIZE,
  FOREIGN_PARK_MS,
  activateLearner,
  adoptOwnerFromQueue,
  applyOutcomes,
  claimBatch,
  countQueue,
  clearActiveLearner,
  getActiveLearner,
  healFutureSchedules,
  markFailed,
  reviveRejected,
  reviveStuck,
  type QueueCounts,
} from './queue';

export interface SyncState extends QueueCounts {
  online: boolean;
  /** Прямо зараз іде відправка. */
  syncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
  /** Черга поставлена на паузу, бо сесія протухла. Дані на місці, чекають входу. */
  blockedByAuth: boolean;
  /** Чия черга зараз вивантажується. null — застосунок ще не сказав, хто увійшов. */
  learnerId: string | null;
  /** Авторитетний стан гаманця з останньої успішної відправки. */
  wallet: AttemptBatchResponse['wallet'] | null;
  /**
   * Стан відрізка гри з останньої відповіді сервера.
   *
   * Потрібен тому, що перерва більше не приїжджає як 403 на всю пачку:
   * тепер про неї каже тіло відповіді. Це НЕ друге джерело правди про
   * час — авторитетне число для екрана перерви як було, так і лишається
   * за `/api/play-window`; тут воно лише для того, щоб екран питань
   * спинив дитину одразу, а не за пів хвилини до наступного опитування.
   */
  playWindow: PlayWindow | null;
}

export type SendAttempts = (
  attempts: QueuedAttempt['payload'][],
) => Promise<AttemptBatchResponse>;

export interface SyncerOptions {
  db: LearnKidsDb;
  /** Транспорт. Підмінюється в тестах — застосунок не має залежати від живого API. */
  send?: SendAttempts;
  /** Період фонового опитування черги. */
  intervalMs?: number;
  batchSize?: number;
  now?: () => number;
  random?: () => number;
  /** Куди віддавати авторитетні результати, коли вони приїхали. */
  onResults?: (results: AttemptResult[], wallet: AttemptBatchResponse['wallet']) => void;
}

export interface FlushOutcome {
  /** Скільки спроб сервер ПРИЙНЯВ. Не «скільки відправили». */
  sent: number;
  failed: number;
  /** Скільки спроб сервер не зарахував поштучно. */
  rejected: number;
  /** Чи лишилося ще щось у черзі — чи варто одразу заходити на наступне коло. */
  remaining: number;
  reason?: 'empty' | 'offline' | 'busy' | 'auth' | 'network' | 'server' | 'foreign' | 'ok';
}

/**
 * Людський підсумок відмов, згрупований за причиною.
 *
 * Групування тут не косметика: пачка на двадцять пʼять з однією й тією ж
 * бідою показала б дитині стіну з двадцяти пʼяти червоних рядків. Замість
 * однієї брехні вийшло б двадцять пʼять правд, які лякають більше.
 */
export function describeRejections(rejected: readonly AttemptRejection[]): string {
  const byCode = new Map<string, { count: number; message: string }>();
  for (const item of rejected) {
    const seen = byCode.get(item.code);
    if (seen) seen.count += 1;
    else byCode.set(item.code, { count: 1, message: humanMessageForCode(item.code, item.message) });
  }
  return [...byCode.values()]
    .map(({ count, message }) => (count > 1 ? `${message} (×${count})` : message))
    .join(' ');
}

const EMPTY_COUNTS: QueueCounts = {
  pending: 0,
  inFlight: 0,
  synced: 0,
  rejected: 0,
  unsent: 0,
};

/**
 * Фоновий синхронізатор офлайн-черги.
 *
 * Життєвий цикл спроби:
 *   pending ──claim──▶ syncing ──вирок «прийнято»──▶ synced
 *      ▲                  │
 *      ├──мережа/401/403 forbidden/5xx (з експоненційною затримкою + джитером)
 *      │                  │
 *      │                  ├──вирок «пізніше»──▶ pending (не раніше retryAt)
 *      │                  └──вирок «остаточно»──▶ rejected (лишається видимою)
 *      └──зміна дитини або «спробувати ще раз»──────┘
 *
 * Ключове: доля рядка тепер визначається ПОШТУЧНИМ вироком у тілі 200, а не
 * HTTP-статусом на всю пачку. Саме статус робив із однієї непридатної спроби
 * вирок над двадцятьма чотирма чесними — і, оскільки черга віддає найстаріші
 * рядки першими, та спроба поверталася в кожну наступну пачку назавжди.
 *
 * Жодна гілка не видаляє рядок і жоден стан не є кінцевим, крім `synced`.
 * Це і є гарантія «нічого не зникне».
 *
 * Другий інваріант, не менш важливий: пристрій один на двох дітей, тож
 * синхронізатор вивантажує чергу ЛИШЕ тієї дитини, що зараз у сесії
 * (`setActiveLearner`). Чужа відповідь не може потрапити в запит навіть
 * теоретично — її не віддасть `claimBatch`.
 */
export class AttemptSyncer {
  private readonly db: LearnKidsDb;
  private readonly send: SendAttempts;
  private readonly intervalMs: number;
  private readonly batchSize: number;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly onResults: SyncerOptions['onResults'];

  private listeners = new Set<(state: SyncState) => void>();
  private state: SyncState;
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  /** Одночасно летить лише одна пачка. Другий виклик підхопить цю ж обіцянку. */
  private inFlight: Promise<FlushOutcome> | null = null;
  /** Прийшов новий запит на flush, поки летіла попередня пачка. */
  private repeatRequested = false;
  private detachers: Array<() => void> = [];

  constructor(options: SyncerOptions) {
    this.db = options.db;
    this.send = options.send ?? ((attempts) => postAttempts(attempts));
    this.intervalMs = options.intervalMs ?? 15_000;
    this.batchSize = options.batchSize ?? BATCH_SIZE;
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? Math.random;
    this.onResults = options.onResults;
    this.state = {
      ...EMPTY_COUNTS,
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      syncing: false,
      lastSyncAt: null,
      lastError: null,
      blockedByAuth: false,
      learnerId: null,
      wallet: null,
      playWindow: null,
    };
  }

  getState(): SyncState {
    return this.state;
  }

  subscribe(listener: (state: SyncState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private setState(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }

  private async refreshCounts(): Promise<void> {
    // Рахуємо рядки лише активного власника: чужі відповіді дитина однаково
    // не може відправити, а число, яке не змінюється від жодної її дії,
    // виглядає як поламаний застосунок.
    const counts = await countQueue(this.db, await getActiveLearner(this.db));
    this.setState(counts);
  }

  /**
   * Тригери синхронізації навмисно надлишкові: старт, поява мережі,
   * повернення на вкладку, кожна відповідь і таймер. Дешевше зробити
   * зайвий безрезультатний прохід, ніж проґавити момент і лишити
   * відповідь дитини на пристрої.
   */
  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;

    // Спроби, що зависли в `syncing` після вбитої вкладки, повертаємо в чергу.
    await reviveStuck(this.db, this.now());
    // Хто працював за пристроєм до перезавантаження вкладки — знає база.
    // Поки застосунок не сказав інше, вивантажуємо чергу саме цієї дитини.
    this.setState({ learnerId: await adoptOwnerFromQueue(this.db, this.now()) });
    await this.refreshCounts();

    if (typeof window !== 'undefined') {
      const onOnline = (): void => {
        this.setState({ online: true });
        this.flushInBackground();
      };
      const onOffline = (): void => this.setState({ online: false });
      const onVisibility = (): void => {
        if (document.visibilityState === 'visible') this.flushInBackground();
      };
      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);
      document.addEventListener('visibilitychange', onVisibility);
      this.detachers.push(
        () => window.removeEventListener('online', onOnline),
        () => window.removeEventListener('offline', onOffline),
        () => document.removeEventListener('visibilitychange', onVisibility),
      );
    }

    this.timer = setInterval(() => this.flushInBackground(), this.intervalMs);
    this.flushInBackground();
  }

  stop(): void {
    this.running = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    for (const detach of this.detachers) detach();
    this.detachers = [];
    // Підписників НЕ чистимо: синхронізатор живе довше за будь-який React-рендер,
    // а StrictMode у dev монтує дерево двічі.
  }

  /** Викликається одразу після запису відповіді в Dexie. */
  notifyRecorded(): void {
    this.inBackground(this.refreshCounts());
    this.flushInBackground();
  }

  /**
   * Оголошує, чия черга тепер активна. Викликати на кожному вході в
   * застосунок і на кожному перемиканні профілю — саме цей виклик робить
   * неможливою відправку відповідей Богдана під сесією Маркусі.
   *
   * Зміна дитини дає другий шанс усьому, що сервер колись відхилив:
   * найімовірніша причина тієї відмови — якраз чужа сесія.
   */
  async setActiveLearner(learnerId: string | null): Promise<void> {
    if (!learnerId) {
      // Батько в кабінеті: власника немає взагалі. Без цього кроку черга
      // лишалася б за останньою дитиною й поїхала б під чужою сесією.
      await clearActiveLearner(this.db);
    }
    if (learnerId) {
      // Оголошуємо власника завжди, не звіряючись із полем стану: власника
      // переставляє ще й `enqueueAttempt` (у тому числі з іншої вкладки), тож
      // дзеркало могло відстати від бази. Пропустити запис через «та сама
      // дитина» означало б лишити чергу за чужим власником. Сам
      // `activateLearner` порівнює з базою і на повторному виклику не робить
      // нічого зайвого.
      await activateLearner(this.db, learnerId, this.now());
    }
    this.setState({ learnerId, lastError: null });
    await this.refreshCounts();
    // Штовхаємо чергу навіть тоді, коли дитина не змінилась: вхід після 401 —
    // це теж момент, коли накопичене має поїхати негайно, а не за таймером.
    this.flushInBackground();
  }

  /** Те саме, але для викликів з UI, яким нема чого чекати на завершення. */
  switchLearner(learnerId: string | null): void {
    this.inBackground(this.setActiveLearner(learnerId));
  }

  /**
   * Явна дія людини: «спробуй ще раз усе, що не прийнялося».
   * Потрібна кнопці в кабінеті батьків — без неї `rejected` лишався б
   * станом без виходу.
   */
  async retryRejected(): Promise<number> {
    const revived = await reviveRejected(this.db, {
      learnerId: this.state.learnerId ?? undefined,
      now: this.now(),
    });
    await this.refreshCounts();
    if (revived > 0) this.flushInBackground();
    return revived;
  }

  /** Після повторного входу знімаємо паузу і одразу відправляємо накопичене. */
  resumeAfterLogin(learnerId?: string | null): void {
    this.setState({ blockedByAuth: false, lastError: null });
    // Вхід — найчастіший момент зміни дитини, тож спершу власник, потім відправка.
    if (learnerId !== undefined) {
      this.inBackground(this.setActiveLearner(learnerId));
      return;
    }
    this.flushInBackground();
  }

  /**
   * Робота, на яку ніхто не чекає. Помилку самого сховища кинути нікуди —
   * вона стала б unhandled rejection і поклала б вкладку разом із чергою.
   * Тому не ковтаємо мовчки, а показуємо її у стані: індикатор для того й є.
   */
  private inBackground(work: Promise<unknown>): void {
    void work.catch((error: unknown) => {
      this.setState({
        syncing: false,
        lastError: error instanceof Error ? error.message : String(error),
      });
    });
  }

  private flushInBackground(): void {
    this.inBackground(this.flush());
  }

  /**
   * Підтягує власника черги з бази. Авторитет — саме база: запис відповіді
   * (`enqueueAttempt`) переставляє власника сам, і синхронізатор має
   * дізнатися про це навіть тоді, коли вхід нікому нічого не повідомив.
   * Поле стану — лише дзеркало для UI.
   */
  private async syncActiveLearner(): Promise<void> {
    const owner = await getActiveLearner(this.db);
    if (owner && owner !== this.state.learnerId) this.setState({ learnerId: owner });
  }

  flush(): Promise<FlushOutcome> {
    if (this.inFlight) {
      this.repeatRequested = true;
      return this.inFlight;
    }
    const run = this.doFlush().finally(() => {
      this.inFlight = null;
      if (this.repeatRequested) {
        this.repeatRequested = false;
        this.flushInBackground();
      }
    });
    this.inFlight = run;
    return run;
  }

  private async doFlush(): Promise<FlushOutcome> {
    if (this.state.blockedByAuth) {
      await this.refreshCounts();
      return { sent: 0, failed: 0, rejected: 0, remaining: this.state.unsent, reason: 'auth' };
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      this.setState({ online: false });
      await this.refreshCounts();
      return { sent: 0, failed: 0, rejected: 0, remaining: this.state.unsent, reason: 'offline' };
    }

    // Власника читаємо перед КОЖНОЮ вибіркою: за пристроєм могла сісти інша
    // дитина, поки летіла попередня пачка.
    await this.syncActiveLearner();

    let batch = await claimBatch(this.db, { limit: this.batchSize, now: this.now() });
    if (batch.length === 0) {
      // Порожньо — але, можливо, лише тому, що годинник пристрою перевели
      // вперед і рядки «дозріють» аж наступного року. Лікуємо і пробуємо ще раз.
      const healed = await healFutureSchedules(this.db, this.now());
      if (healed > 0) {
        batch = await claimBatch(this.db, { limit: this.batchSize, now: this.now() });
      }
    }
    if (batch.length === 0) {
      await this.refreshCounts();
      return { sent: 0, failed: 0, rejected: 0, remaining: this.state.unsent, reason: 'empty' };
    }

    this.setState({ syncing: true });
    const ids = batch.map((row) => row.id);

    try {
      const response = await this.send(batch.map((row) => row.payload));
      /*
       * `ids` тут — усе, що поїхало, а не «все, що прийнято»: розібратися,
       * кому з них що дісталося, — робота `applyOutcomes`. Старий код ставив
       * `synced` усім id пачки, і незарахована спроба тихо зависала як
       * «ще не перевірена» довіку.
       */
      const counts = await applyOutcomes({
        db: this.db,
        ids,
        results: response.results,
        rejected: response.rejected,
        now: this.now(),
        random: this.random,
      });
      this.onResults?.(response.results, response.wallet);
      this.setState({
        syncing: false,
        lastSyncAt: this.now(),
        lastError: counts.rejected > 0 ? describeRejections(response.rejected) : null,
        online: true,
        wallet: response.wallet,
        playWindow: response.playWindow ?? this.state.playWindow,
      });
      await this.refreshCounts();
      return {
        sent: counts.accepted,
        failed: 0,
        rejected: counts.rejected,
        remaining: this.state.unsent,
        reason: 'ok',
      };
    } catch (error) {
      const outcome = await this.handleFailure(ids, error);
      await this.refreshCounts();
      return { ...outcome, remaining: this.state.unsent };
    }
  }

  /**
   * Біди ВСЬОГО запиту — і тільки вони.
   *
   * Відколи вирок про окрему спробу приїжджає в тілі 200, сюди доходить лише
   * те, що справді стосується всієї пачки: не увійшли (401), пачка поїхала
   * під чужим профілем (403 forbidden), сервер ліг (5xx), мережа зникла.
   * Гілка «інші 4xx → rejected» лишається запобіжником на випадок старого
   * сервера чи биття контракту — сама вона більше не має спрацьовувати.
   */
  private async handleFailure(
    ids: string[],
    error: unknown,
  ): Promise<Omit<FlushOutcome, 'remaining'>> {
    // 401 — сесія протухла. Чергу НЕ чіпаємо і не штрафуємо затримкою:
    // відповіді дитини поїдуть відразу після повторного входу.
    if (error instanceof ApiHttpError && error.isUnauthorized) {
      await markFailed({
        db: this.db,
        ids,
        error: 'Сесія завершилась — увійди ще раз',
        bumpTries: false,
        delayMs: 0,
        now: this.now(),
      });
      this.setState({
        syncing: false,
        blockedByAuth: true,
        lastError: 'Потрібно увійти знову',
      });
      return { sent: 0, failed: ids.length, rejected: 0, reason: 'auth' };
    }

    // 403 «forbidden» — сервер каже «це не твої відповіді». Найчастіша
    // причина: пачку взяли під сесією іншої дитини. Це біда маршрутизації,
    // а не даних: самі відповіді бездоганні й поїдуть, щойно увійде їхній
    // власник. Позначати їх остаточно відхиленими означало б спалити чужу роботу.
    //
    // Розрізняємо саме за КОДОМ, а не за статусом: під 403 сервер шле ще й
    // «поза розкладом», «перерва» та «денний ліміт». Ті вирішуються часом
    // відповіді, який уже не зміниться, — паркувати їх «до входу власника»
    // означало б стукати в сервер кожні пʼять хвилин довіку й брехати про
    // причину. Їм дорога в `rejected`: видно людині, оборотно кнопкою.
    if (error instanceof ApiHttpError && error.code === API_ERROR_CODES.FORBIDDEN) {
      await markFailed({
        db: this.db,
        ids,
        error: 'Ці відповіді належать іншому профілю',
        bumpTries: false,
        delayMs: FOREIGN_PARK_MS,
        now: this.now(),
      });
      this.setState({
        syncing: false,
        lastError: 'Відповіді іншого профілю чекають на його вхід',
      });
      return { sent: 0, failed: ids.length, rejected: 0, reason: 'foreign' };
    }

    // 4xx (крім 401/403/408/429) повторювати марно — але й ховати не можна.
    // Штатно сюди більше не потрапляють ані «перерва», ані «поза розкладом»:
    // вони стали поштучними вироками в тілі 200.
    if (error instanceof ApiHttpError && error.isPermanent) {
      await markFailed({
        db: this.db,
        ids,
        error: `${error.code}: ${error.message}`,
        permanent: true,
        now: this.now(),
      });
      this.setState({
        syncing: false,
        lastError: `Сервер відхилив ${ids.length} відповідь(ей): ${error.message}`,
      });
      return { sent: 0, failed: ids.length, rejected: ids.length, reason: 'server' };
    }

    const isNetwork = error instanceof NetworkError;
    await markFailed({
      db: this.db,
      ids,
      error: error instanceof Error ? error.message : String(error),
      now: this.now(),
      random: this.random,
    });
    this.setState({
      syncing: false,
      online: isNetwork ? false : this.state.online,
      lastError: isNetwork
        ? 'Немає інтернету — відповіді чекають у черзі'
        : 'Сервер не відповідає — спробуємо ще раз',
    });
    return { sent: 0, failed: ids.length, rejected: 0, reason: isNetwork ? 'network' : 'server' };
  }

  /** Проганяє чергу до кінця. Використовується в тестах і на екрані підсумку. */
  async drain(maxRounds = 20): Promise<FlushOutcome> {
    let last: FlushOutcome = { sent: 0, failed: 0, rejected: 0, remaining: 0, reason: 'empty' };
    for (let round = 0; round < maxRounds; round += 1) {
      last = await this.flush();
      // Рух черги — це і прийняті, і відхилені: отруйна спроба виходить із
      // голови черги, і наступне коло везе чесні відповіді за нею. Спинятись
      // на `sent === 0` означало б лишати їх до наступного таймера.
      if (last.sent === 0 && last.rejected === 0) break;
      if (last.remaining === 0) break;
    }
    return last;
  }
}
